import React, { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Spin, Space, Row, Col, Input, Select, DatePicker, Button, Switch } from 'antd';
import { App as AntdApp } from 'antd';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import CalculationHistory from '../components/CalculationHistory';
import './HistoryPage.less';
// 移除历史页直接导出 PDF 的依赖
// import { createRoot } from 'react-dom/client';
// import jsPDF from 'jspdf';
// import html2canvas from 'html2canvas';
import { buildMarkdownReport, downloadTextFile, getCalculationFormula, formatResultLabel, getProtectionTypeName, exportElementToPdf, exportElementToHtml, exportElementToWord, captureElementToPngDataUrl, resolveFilenamePrefix, buildFinalBaseName } from '../utils/exportUtils';
import { calculateProtectionSettings } from '../utils/protectionCalculations';
import { createRoot } from 'react-dom/client';
import ExportReport from '../components/ExportReport';
import { enqueue } from '../utils/offlineQueue';
import { generateVersionStamp, generateRandomToken, buildShareUrlSigned, buildShareUrlPreferShort } from '../utils/shareUtils';
import { useI18n } from '../i18n/i18n';
import { getHistoryCache, setHistoryCache, getHistoryCacheInfo, clearHistoryCache } from '../utils/historyCache';
import TccOverlayModal from '../components/TccOverlayModal';

const { Title, Text } = Typography;

const HistoryPage = ({ userId }) => {
  const { message } = AntdApp.useApp();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useI18n();
  const [cacheTs, setCacheTs] = useState(null);
  const [usedCache, setUsedCache] = useState(false);

  // 筛选、排序、分页状态
  const [filterType, setFilterType] = useState(undefined);
  const [searchName, setSearchName] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [sortKey, setSortKey] = useState('time_desc'); // time_desc, time_asc, name_asc, name_desc, project_asc/desc, line_asc/desc
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [projectFilter, setProjectFilter] = useState('');
  const [lineFilter, setLineFilter] = useState('');
  const [tagsFilter, setTagsFilter] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tccVisible, setTccVisible] = useState(false);
  const [overlayRecords, setOverlayRecords] = useState([]);

  // 统一文件名前缀模板（来自本地设置，或使用默认）
  const fileNamePrefixTemplate = useMemo(() => {
    try {
      const opts = JSON.parse(localStorage.getItem('exportOptions') || '{}');
      const val = (opts.fileNamePrefix || '').trim();
      return val || '{name}-{project}-{line}-{date}';
    } catch (_) {
      return '{name}-{project}-{line}-{date}';
    }
  }, []);
  // 废弃的本地实现（与 utils 重复），避免遮蔽统一助手
  const buildFinalBaseName_local_removed = (baseName, ctx = {}) => {
    const n = String(baseName || '').trim();
    const prefixResolved = resolveFilenamePrefix(fileNamePrefixTemplate, { name: n, project: ctx.project || '', line: ctx.line || '' });
    const prefixTrim = (prefixResolved || '').trim();
    const safeName = n
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 50);
    if (!prefixTrim) return safeName || 'unnamed';
    const includesNameInPrefix = (
      prefixTrim === safeName ||
      new RegExp(`(^|[\\-_])${safeName}([\\-_]|$)`).test(prefixTrim)
    );
    return includesNameInPrefix ? prefixTrim : `${prefixTrim}-${safeName}`;
  };

  // 获取计算历史记录
  useEffect(() => {
    if (!userId) {
      setHistory([]);
      setLoading(false);
      return;
    }
    fetchCalculationHistory();
  }, [userId]);

  const fetchCalculationHistory = async () => {
    if (!userId) return; // 未登录不发起查询，避免 uuid 错误
    // 离线模式：尝试使用本地缓存历史
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const info = getHistoryCacheInfo();
      const cached = info.list;
      if (cached && cached.length > 0) {
        setHistory(cached);
        setCacheTs(info.ts || null);
        setUsedCache(true);
        message.info(t('offline.history_cache_used'));
      } else {
        setCacheTs(info.ts || null);
        setUsedCache(false);
        message.warning(t('offline.history_cache_empty'));
      }
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('calculations_23813')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      console.log('calculations_23813',data);

      if (error) {
        message.error(t('history.fetch.failed_prefix') + error.message);
        return;
      }
      

      setHistory(data || []);
      // 写入本地缓存，供离线模式使用
      setHistoryCache(data || []);
      setCacheTs(Date.now());
      setUsedCache(false);
    } catch (error) {
      console.error('获取计算历史异常:', error);
      message.error(t('history.fetch.exception_prefix') + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      // 离线：入队并本地乐观删除
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        enqueue('delete_calculation', { id });
        setHistory(prev => prev.filter(item => item.id !== id));
        message.success(t('history.delete.offline_enqueued_local'));
        return;
      }
      const { error } = await supabase
        .from('calculations_23813')
        .delete()
        .eq('id', id);

      if (error) {
        // 网络失败：入队并本地乐观删除
        enqueue('delete_calculation', { id });
        setHistory(prev => prev.filter(item => item.id !== id));
        message.warning(t('history.delete.failure_enqueued_local'));
        return;
      }

      // 成功：本地删除并提示
      setHistory(prev => prev.filter(item => item.id !== id));
      message.success(t('history.delete.success'));
    } catch (error) {
      console.error('删除记录异常:', error);
      message.error(t('history.delete.exception_prefix') + (error.message || '未知错误'));
    }
  };

  const handleView = (record) => {
    navigate('/calculation', { state: { mode: 'view', record } });
  };

  const handleRecalculate = (record) => {
    navigate('/calculation', { state: { mode: 'recalculate', record } });
  };

  const handleExport = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try {
          results = calculateProtectionSettings(parameters);
        } catch (e) {
          results = {};
        }
      }
      const remark = record?.result?.remark || '';
      const name = record?.result?.name || '';
      const extras = record?.result?.extras || {};
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';

      // 临时渲染以捕获曲线截图（供 Markdown 嵌入）
      let chartImageDataUrl = null;
      try {
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '-9999px';
        document.body.appendChild(container);
        const root = createRoot(container);
        root.render(
          <ExportReport 
            name={name} 
            type={type} 
            parameters={parameters} 
            results={results}
            remark={remark}
            extras={extras}
          />
        );
        await new Promise((r) => setTimeout(r, 60));
        const chartEl = container.querySelector('.h-curve-svg') || container.querySelector('svg');
        if (chartEl) chartImageDataUrl = await captureElementToPngDataUrl(chartEl);
        root.unmount();
        document.body.removeChild(container);
      } catch (_) {}

      const { md } = buildMarkdownReport({ 
        parameters, 
        results, 
        remark, 
        name, 
        extras: { ...extras, chartImageDataUrl, versionStamp: generateVersionStamp(), filenamePrefixResolved: resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extras.project || '', line: extras.line || '' }) } 
      }, { t });
      const prefixResolvedMd = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extras.project || '', line: extras.line || '' });
      const finalBase = buildFinalBaseName(name, prefixResolvedMd);
      downloadTextFile(md, `${finalBase}.md`, 'text/markdown;charset=utf-8');
      message.success(t('history.export.md.success'));
    } catch (error) {
      message.error(t('history.export.failure_prefix') + (error.message || '未知错误'));
    }
  };

  const handleCopyJson = async (record) => {
    try {
      const text = JSON.stringify(record, null, 2);
      await navigator.clipboard.writeText(text);
      message.success(t('history.copy_json.success'));
    } catch (error) {
      message.error(t('history.copy_json.failure_prefix') + (error.message || '未知错误'));
    }
  };

  const handleUpdateMeta = async (id, patch) => {
    try {
      const target = history.find(h => h.id === id);
      if (!target) return;
      const currentExtras = target.result?.extras || {};
      let nextExtras = { ...currentExtras, ...(patch.extras || {}) };
      // 处理标签的字符串输入（逗号分隔）
      if (typeof nextExtras.tags === 'string') {
        nextExtras.tags = nextExtras.tags.split(',').map(s => s.trim()).filter(Boolean);
      }
      const newResult = {
        ...(target.result || {}),
        name: patch.name !== undefined ? patch.name : (target.result?.name || ''),
        remark: patch.remark !== undefined ? patch.remark : (target.result?.remark || ''),
        extras: nextExtras
      };
      // 离线时直接入队并本地乐观更新
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        enqueue('update_meta', { id, patch: { result: newResult } });
        setHistory(prev => prev.map(item => item.id === id ? { ...item, result: newResult } : item));
        message.success(t('history.update.offline_enqueued_local'));
        return;
      }
      const { data, error } = await supabase
        .from('calculations_23813')
        .update({ result: newResult })
        .eq('id', id)
        .select()
        .single();
      if (error) {
        // 网络失败也进行离线排队处理
        enqueue('update_meta', { id, patch: { result: newResult } });
        setHistory(prev => prev.map(item => item.id === id ? { ...item, result: newResult } : item));
        message.warning(t('history.update.failure_enqueued_local'));
        return;
      }
      setHistory(prev => prev.map(item => item.id === id ? data : item));
      message.success(t('history.update.success'));
    } catch (error) {
      message.error(t('history.update.exception_prefix') + (error.message || '未知错误'));
    }
  };

  // 历史记录导出为 PDF
  const handleExportPdf = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try {
          results = calculateProtectionSettings(parameters);
        } catch (e) {
          results = {};
        }
      }
      const name = (record?.result?.name || '').trim();
      const remark = record?.result?.remark || '';
      const extras = record?.result?.extras || {};
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';

      // 临时导出容器（React 组件渲染）
      const container = document.createElement('div');
      container.className = 'history-export-container print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport 
          name={name} 
          type={type} 
          parameters={parameters} 
          results={results}
          remark={remark}
          extras={extras}
          className="print-layout"
        />
      );
      await new Promise((r) => setTimeout(r, 50));
      const prefixResolvedPdf = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extras.project || '', line: extras.line || '' });
      const finalBase = buildFinalBaseName(name, prefixResolvedPdf);
      await exportElementToPdf({ element: container, name: finalBase, type, title: t('history.export.report.title'), appendTimestamp: false, t });
      message.success(t('history.export.pdf.success'));
      root.unmount();
      document.body.removeChild(container);
    } catch (error) {
      message.error(t('history.export.pdf.failure_prefix') + (error.message || '未知错误'));
    }
  };

  // 历史记录打印视图（仅打印布局）
  const handlePrintReport = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try {
          results = calculateProtectionSettings(parameters);
        } catch (e) {
          results = {};
        }
      }
      const name = (record?.result?.name || '').trim();
      const remark = record?.result?.remark || '';
      const extras = record?.result?.extras || {};
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';
      const container = document.createElement('div');
      container.className = 'history-export-container print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport 
          name={name}
          type={type}
          parameters={parameters}
          results={results}
          remark={remark}
          extras={extras}
          className="print-layout"
        />
      );
      await new Promise((r) => setTimeout(r, 80));
      window.print();
      setTimeout(() => {
        try { root.unmount(); } catch (_) {}
        try { document.body.removeChild(container); } catch (_) {}
      }, 300);
      message.success(t('history.print.opened'));
    } catch (error) {
      message.error(t('history.print.failure_prefix') + (error.message || '未知错误'));
    }
  };

  // 计算过滤与排序后的列表
  const filteredSorted = useMemo(() => {
    let list = Array.isArray(history) ? [...history] : [];
    // 过滤类型
    if (filterType) {
      list = list.filter(item => (item.calculation_type || item.parameters?.protectionType) === filterType);
    }
    // 项目/线路过滤
    if (projectFilter && projectFilter.trim()) {
      const pf = projectFilter.trim().toLowerCase();
      list = list.filter(item => (item.result?.extras?.project || '').toLowerCase().includes(pf));
    }
    if (lineFilter && lineFilter.trim()) {
      const lf = lineFilter.trim().toLowerCase();
      list = list.filter(item => (item.result?.extras?.line || '').toLowerCase().includes(lf));
    }
    // 标签过滤（任一命中）
    if (Array.isArray(tagsFilter) && tagsFilter.length > 0) {
      list = list.filter(item => {
        const tags = item.result?.extras?.tags || [];
        return tagsFilter.some(t => tags.includes(t));
      });
    }
    // 过滤名称
    if (searchName && searchName.trim()) {
      const q = searchName.trim().toLowerCase();
      list = list.filter(item => (item.result?.name || '').toLowerCase().includes(q));
    }
    // 过滤时间范围
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const start = new Date(dateRange[0].startOf('minute').toDate());
      const end = new Date(dateRange[1].endOf('minute').toDate());
      list = list.filter(item => {
        const created = new Date(item.created_at);
        return created >= start && created <= end;
      });
    }
    // 归档过滤（默认隐藏归档）
    if (!showArchived) {
      list = list.filter(item => !item.result?.extras?.archived);
    }
    // 置顶收藏
    list.sort((a, b) => {
      const favA = a.result?.extras?.favorite ? 1 : 0;
      const favB = b.result?.extras?.favorite ? 1 : 0;
      if (favB - favA !== 0) return favB - favA;
      // 次级排序
      if (sortKey === 'time_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortKey === 'time_asc') return new Date(a.created_at) - new Date(b.created_at);
      const an = (a.result?.name || '').toLowerCase();
      const bn = (b.result?.name || '').toLowerCase();
      if (sortKey === 'name_asc') return an.localeCompare(bn);
      if (sortKey === 'name_desc') return bn.localeCompare(an);
      const ap = (a.result?.extras?.project || '').toLowerCase();
      const bp = (b.result?.extras?.project || '').toLowerCase();
      if (sortKey === 'project_asc') return ap.localeCompare(bp);
      if (sortKey === 'project_desc') return bp.localeCompare(ap);
      const al = (a.result?.extras?.line || '').toLowerCase();
      const bl = (b.result?.extras?.line || '').toLowerCase();
      if (sortKey === 'line_asc') return al.localeCompare(bl);
      if (sortKey === 'line_desc') return bl.localeCompare(al);
      return 0;
    });
    return list;
  }, [history, filterType, searchName, dateRange, sortKey, projectFilter, lineFilter, tagsFilter, showArchived]);

  // 当前页数据
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const resetFilters = () => {
    setFilterType(undefined);
    setSearchName('');
    setDateRange([]);
    setSortKey('time_desc');
    setCurrentPage(1);
    setProjectFilter('');
    setLineFilter('');
    setTagsFilter([]);
    setShowArchived(false);
    setSelectedIds([]);
  };

  // 组件内定义选择与批量操作，避免作用域问题
  const handleSelectChange = (ids) => {
    setSelectedIds(Array.isArray(ids) ? ids : []);
  };

  const handleBulkExport = async (ids) => {
    const byId = new Map((history || []).map(r => [r.id, r]));
    for (const id of (ids || [])) {
      const rec = byId.get(id);
      if (rec) {
        try { await handleExport(rec); } catch (_) {}
      }
    }
  };

  const handleBulkFavorite = async (ids) => {
    for (const id of (ids || [])) {
      try { await handleUpdateMeta(id, { extras: { favorite: true } }); } catch (_) {}
    }
  };

  const handleBulkArchive = async (ids) => {
    for (const id of (ids || [])) {
      try { await handleUpdateMeta(id, { extras: { archived: true } }); } catch (_) {}
    }
  };

  const handleBulkTccOverlay = (ids = []) => {
    const byId = new Map((history || []).map(r => [r.id, r]));
    const recs = (ids || []).map(id => byId.get(id)).filter(Boolean);
    setOverlayRecords(recs);
    setTccVisible(true);
  };

  const handleBulkEditProject = async (ids = [], project = '') => {
    for (const id of (ids || [])) {
      try { await handleUpdateMeta(id, { extras: { project } }); } catch (_) {}
    }
    message.success(t('history.update.success'));
  };

  const handleBulkEditLine = async (ids = [], line = '') => {
    for (const id of (ids || [])) {
      try { await handleUpdateMeta(id, { extras: { line } }); } catch (_) {}
    }
    message.success(t('history.update.success'));
  };

  // 组件内定义导出与分享，复用 message/t 等上下文
  const handleExportHtml = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try { results = calculateProtectionSettings(parameters); } catch (e) { results = {}; }
      }
      const name = (record?.result?.name || '').trim();
      const remark = record?.result?.remark || '';
      const extras = { ...(record?.result?.extras || {}), versionStamp: generateVersionStamp() };
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';
      const container = document.createElement('div');
      container.className = 'history-export-container print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport 
          name={name} 
          type={type} 
          parameters={parameters} 
          results={results}
          remark={remark}
          extras={extras}
          className="print-layout"
        />
      );
      await new Promise((r) => setTimeout(r, 60));
      const prefixResolvedHtml = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extras.project || '', line: extras.line || '' });
      const finalBase = buildFinalBaseName(name, prefixResolvedHtml);
      exportElementToHtml({ element: container, name: finalBase, title: t('history.export.report.title'), appendTimestamp: false });
      root.unmount();
      document.body.removeChild(container);
      message.success(t('history.export.html.success'));
    } catch (error) {
      message.error(t('history.export.html.failure_prefix') + (error.message || '未知错误'));
    }
  };

  const handleExportWord = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try { results = calculateProtectionSettings(parameters); } catch (e) { results = {}; }
      }
      const name = (record?.result?.name || '').trim();
      const remark = record?.result?.remark || '';
      const extras = { ...(record?.result?.extras || {}), versionStamp: generateVersionStamp() };
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';
      const container = document.createElement('div');
      container.className = 'history-export-container print-layout';
      document.body.appendChild(container);
      const root = createRoot(container);
      root.render(
        <ExportReport 
          name={name} 
          type={type} 
          parameters={parameters} 
          results={results}
          remark={remark}
          extras={extras}
          className="print-layout"
        />
      );
      await new Promise((r) => setTimeout(r, 60));
      const prefixResolvedWord = resolveFilenamePrefix(fileNamePrefixTemplate, { name, project: extras.project || '', line: extras.line || '' });
      const finalBase = buildFinalBaseName(name, prefixResolvedWord);
      exportElementToWord({ element: container, name: finalBase, title: t('history.export.report.title'), appendTimestamp: false });
      root.unmount();
      document.body.removeChild(container);
      message.success(t('history.export.word.success'));
    } catch (error) {
      message.error(t('history.export.word.failure_prefix') + (error.message || '未知错误'));
    }
  };

  const handleShareLink = async (record) => {
    try {
      const parameters = record?.parameters || {};
      let results = record?.results;
      if (!results && parameters) {
        try { results = calculateProtectionSettings(parameters); } catch (e) { results = {}; }
      }
      const name = (record?.result?.name || '').trim();
      const remark = record?.result?.remark || '';
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const extras = { ...(record?.result?.extras || {}), versionStamp: generateVersionStamp(), expiresAt };
      const type = record?.calculation_type || parameters?.protectionType || 'unknown';
      const payload = { name, type, parameters, results, remark, extras };
      const token = generateRandomToken(24);
      const url = await buildShareUrlPreferShort({ payload, token, expiresAt, policy: { requiresLogin: false, maxUses: 1 }, preferShort: true });
      try {
        await navigator.clipboard.writeText(url);
        message.success(t('share.copy.success'));
      } catch (_) {
        message.info(t('share.copy.info_fallback'));
      }
      try { window.open(url, '_blank'); } catch (_) {}
    } catch (error) {
      message.error(t('share.generate.failure_prefix') + (error.message || '未知错误'));
    }
  };

  if (!userId) {
    return (
      <div className="history-page">
        <div className="header">
          <Title level={2}>{t('history.title')}</Title>
        </div>
        <Card>
          <Text type="secondary">{t('history.not_logged_in')}</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="header">
        <Title level={2}>{t('history.title')}</Title>
        {usedCache && cacheTs && (
          <Text type="secondary">{t('history.cache.timestamp_prefix')}{new Date(cacheTs).toLocaleString()}</Text>
        )}
        {usedCache && (
          <Button size="small" danger style={{ marginLeft: 12 }} onClick={() => {
            try {
              clearHistoryCache();
              setHistory([]);
              setUsedCache(false);
              message.success(t('history.cache.cleared'));
            } catch (_) {
              message.error(t('history.cache.clear_failed'));
            }
          }}>
            {t('history.cache.clear_button')}
          </Button>
        )}
      </div>

      {/* 筛选与排序栏 */}
      <Card style={{ marginBottom: 12 }}>
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder={t('history.filter.protection_type')}
              style={{ width: '100%' }}
              value={filterType}
              onChange={(val) => { setFilterType(val); setCurrentPage(1); }}
              options={[
                { value: 'overcurrent', label: t('protection.type.overcurrent') },
                { value: 'distance', label: t('protection.type.distance') },
                { value: 'differential', label: t('protection.type.differential') },
                { value: 'directional', label: t('protection.type.directional') },
                { value: 'zero_sequence', label: t('protection.type.zero_sequence') },
                { value: 'longitudinal', label: t('protection.type.longitudinal') }                   
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              allowClear
              placeholder={t('history.search.by_name')}
              value={searchName}
              onChange={(e) => { setSearchName(e.target.value); setCurrentPage(1); }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              allowClear
              placeholder={t('history.filter.project')}
              value={projectFilter}
              onChange={(e) => { setProjectFilter(e.target.value); setCurrentPage(1); }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              allowClear
              placeholder={t('history.filter.line')}
              value={lineFilter}
              onChange={(e) => { setLineFilter(e.target.value); setCurrentPage(1); }}
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(v) => { setDateRange(v || []); setCurrentPage(1); }}
              showTime={false}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              mode="multiple"
              allowClear
              placeholder={t('history.filter.tags')}
              style={{ width: '100%' }}
              value={tagsFilter}
              onChange={(vals) => { setTagsFilter(vals); setCurrentPage(1); }}
              options={(history || []).reduce((acc, item) => {
                const tags = item?.result?.extras?.tags || [];
                tags.forEach(t => { if (!acc.find(x => x.value === t)) acc.push({ value: t, label: t }); });
                return acc;
              }, [])}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              value={sortKey}
              onChange={(v) => setSortKey(v)}
              options={[
                { value: 'time_desc', label: t('history.sort.time_desc') },
                { value: 'time_asc', label: t('history.sort.time_asc') },
                { value: 'name_asc', label: t('history.sort.name_asc') },
                { value: 'name_desc', label: t('history.sort.name_desc') },
                { value: 'project_asc', label: t('history.sort.project_asc') },
                { value: 'project_desc', label: t('history.sort.project_desc') },
                { value: 'line_asc', label: t('history.sort.line_asc') },
                { value: 'line_desc', label: t('history.sort.line_desc') }
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Switch checked={showArchived} onChange={(v) => { setShowArchived(v); setCurrentPage(1); }} />
              <Text type="secondary">{t('history.filter.show_archived')}</Text>
            </Space>
          </Col>
          <Col xs={24}>
            <Space>
              <Button onClick={resetFilters}>{t('history.filter.reset')}</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <CalculationHistory 
          history={pageData} 
          loading={loading} 
          onDelete={handleDelete}
          onView={handleView}
          onRecalculate={handleRecalculate}
          onExport={handleExport}
          onExportPdf={handleExportPdf}
          onPrintReport={handlePrintReport}
          onCopyJson={handleCopyJson}
          onUpdateMeta={handleUpdateMeta}
          onExportHtml={handleExportHtml}
          onExportWord={handleExportWord}
          onShareLink={handleShareLink}
          selectedIds={selectedIds}
          onSelectChange={handleSelectChange}
          onBulkExport={handleBulkExport}
          onBulkFavorite={handleBulkFavorite}
          onBulkArchive={handleBulkArchive}
          onBulkTccOverlay={handleBulkTccOverlay}
          onBulkEditProject={handleBulkEditProject}
          onBulkEditLine={handleBulkEditLine}
          pagination={{
            total: filteredSorted.length,
            current: currentPage,
            pageSize,
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            showSizeChanger: true,
            pageSizeOptions: ['5','10','20','50']
          }}
        />
        {(function(){
          // 单选详情预览：自动整定摘要
          if (!Array.isArray(selectedIds) || selectedIds.length !== 1) return null;
          const id = selectedIds[0];
          const byId = new Map((history || []).map(r => [r.id, r]));
          const rec = byId.get(id);
          if (!rec) return null;
          const extras = rec?.result?.extras || {};
          const summary = extras?.autoTuningParamSummary;
          const events = Array.isArray(extras?.autoTuningLogEvents) ? extras.autoTuningLogEvents : [];
          if (!summary && events.length === 0) return null;
          let lastTimeText = '';
          try { lastTimeText = summary?.lastTime ? new Date(summary.lastTime).toLocaleString('zh-CN') : ''; } catch(_) {}
          const lastActionText = summary?.lastAction === 'apply' ? '应用建议' : (summary?.lastAction === 'rollback' ? '回滚至快照' : (summary?.lastAction || ''));
          return (
            <Card style={{ marginTop: 12 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={5}>自动整定详情</Title>
                <div>
                  <Text type="secondary">事件数：</Text>
                  <Text>{events.length}</Text>
                </div>
                {summary ? (
                  <>
                    <div>
                      <Text type="secondary">应用次数：</Text>
                      <Text>{summary.applyCount ?? 0}</Text>
                    </div>
                    <div>
                      <Text type="secondary">回滚次数：</Text>
                      <Text>{summary.rollbackCount ?? 0}</Text>
                    </div>
                    {(lastActionText || lastTimeText) ? (
                      <div>
                        <Text type="secondary">最近操作：</Text>
                        <Text>{lastActionText}{lastTimeText ? `（${lastTimeText}）` : ''}</Text>
                      </div>
                    ) : null}
                    {(Array.isArray(summary?.changedParams) && summary.changedParams.length > 0) ? (
                      <div>
                        <Text type="secondary">汇总参数：</Text>
                        <Text>
                          {summary.changedParams.map((c, idx) => (
                            <span key={`hp-sum-${idx}`}>{(c?.label || c?.key)}: {c?.to}{idx < summary.changedParams.length - 1 ? '； ' : ''}</span>
                          ))}
                        </Text>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </Space>
            </Card>
          );
        })()}
        <TccOverlayModal visible={tccVisible} onClose={() => setTccVisible(false)} records={overlayRecords} />
      </Spin>
    </div>
  );
};

export default HistoryPage;
